import FunctionHelper from "./FunctionHelper";

const xmlParser: DOMParser = new DOMParser();
const xmlSerializer: XMLSerializer = new XMLSerializer();
const xmlEvaluator: XPathEvaluator = new XPathEvaluator();

export default class XmlHelper {
	/**
	 * Escape string value to be used as xml content
	 */
	public static encode(value: string): string {
		let escapes: Map<string, string> = new Map<string, string>([
			["<", "&lt;"],
			[">", "&gt;"],
			["&", "&amp;"],
			["'", "&apos;"],
			["\"", "&quot;"]
		])

		let result = value;

		for (let [key, replacement] of escapes.entries()) {
			result = result.replaceAll(key, replacement);
		}

		return result;
	}

	/**
	 * Decodes encoded xml content
	 */
	public static decode(value: string): string {
		let escapes: Map<string, string> = new Map<string, string>([
			["&lt;", "<"],
			["&gt;", ">"],
			["&amp;", "&"],
			["&apos;", "'"],
			["&quot;", "\""]
		])

		let result = value;

		for (let [key, replacement] of escapes.entries()) {
			result = result.replaceAll(key, replacement);
		}

		return result;
	}

	/**
	 * Prepares a string value to be used as xml tag name, by removing illegal characters
	 * (https://www.w3.org/TR/REC-xml/#NT-NameStartChar)
	 */
	public static prepareForXmlTagName(value: string): string {
		let result: string = "";

		if (FunctionHelper.isEmpty(value)) {
			return "";
		}

		let isStartChar = function (code: number): boolean {
			return code === 0x3a || (code >= 0x41 && code <= 0x5a) || code === 0x5f || (code >= 0x61 && code <= 0x7a) ||
				(code >= 0xc0 && code <= 0xd6) || (code >= 0xd8 && code <= 0xf6) || (code >= 0xf8 && code <= 0x2ff) ||
				(code >= 0x380 && code <= 0x37d) || (code >= 0x37f && code <= 0x1fff) ||
				(code >= 0x200c && code <= 0x200d) || (code >= 0x2070 && code <= 0x218f) ||
				(code >= 0x2c00 && code <= 0x2fef) || (code >= 0x3001 && code <= 0xd7ff) ||
				(code >= 0xf900 && code <= 0xfdcf) || (code >= 0xfdf0 && code <= 0xfffd) ||
				(code >= 0x10000 && code <= 0xeffff)
		}

		let isNameChar = function (code: number) {
			return isStartChar(code) || code === 0x2d || code === 0x2e || (code >= 0x30 && code <= 0x39) ||
				code === 0xb7 || (code >= 0x300 && code <= 0x36f) || (code >= 0x203f && code <= 0x2040);
		}

		let hasStartChar: boolean = false;
		for (let i = 0; i < value.length; i++) {
			let code: number = value.charCodeAt(i);

			if (!hasStartChar) {
				if (!isStartChar(code)) {
					continue;
				}

				hasStartChar = true;
			} else if (!isNameChar(code)) {
				continue;
			}

			result += value.charAt(i);
		}

		return result;
	}

	public static findByXPath(contextNode: Node, xpath: string): Node | null {
		// let result = SaxonJS.XPath.evaluate(xpath, contextNode);
		// return result;

		let xPathExpression: XPathExpression = xmlEvaluator.createExpression(xpath, (prefix) => {
			return contextNode.lookupNamespaceURI(prefix);
		});
		let xPathResult: XPathResult = xPathExpression.evaluate(contextNode, XPathResult.ANY_TYPE);
		return xPathResult.iterateNext();
	}

	public static findAllByXPath(contextNode: Node, xpath: string): Array<Node> {
		let xPathExpression: XPathExpression = xmlEvaluator.createExpression(xpath, (prefix) => {
			return contextNode.lookupNamespaceURI(prefix);
		});
		let xPathResult: XPathResult = xPathExpression.evaluate(contextNode, XPathResult.ANY_TYPE);

		let results: Array<Node> = [];

		let currentElement: Node | null = xPathResult.iterateNext();
		while (FunctionHelper.exists(currentElement)) {
			results.push(currentElement!);
			currentElement = xPathResult.iterateNext();
		}

		return results;
	}

	public static fromString(value: string): XMLDocument {
		return xmlParser.parseFromString(value, "text/xml");
	}

	public static toString(contextNode: Node): string {
		return xmlSerializer.serializeToString(contextNode)
	}

	/**
	 * Deletes a html element or html elements
	 */
	public static delete(element: Node | Array<Node> | null | undefined): void {
		if (typeof element === "undefined" || element === null) {
			return;
		}

		let target: Array<Node> = (!Array.isArray(element)) ? [element] : element;

		for (let entry of target.values()) {
			if (entry.parentNode !== null) {
				entry.parentNode.removeChild(entry);
			}
		}
	}
}
